import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Grid2,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  Typography
} from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { UserOrEmailMapping } from "../../features/Admin/typings/admin.types";
import { ReactComponent as ConferenceIcon } from "../../features/Booking-Form/assets/conference.svg";
import {
  BookingType,
  NotSerializedBookingInputs
} from "../../features/Booking-Form/typings/booking-inputs";
import { visitorRequestsPlaceOrRoom } from "../../features/Booking-Form/functions/visitor-registration.functions";
import { TeamMember } from "../../features/Booking-Form/typings/team-member";
import { PricingModel } from "../../features/Login/typings/login.types";
import { fetchLocationAvailability } from "../../utils/axios-requests";
import FormConfirmData from "./FormConfirmData/form-confirm-data.component";
import FormConfirmFloorPlan from "./FormConfirmFloorPlan/form-confirm-floor-plan.component";
import FormConfirmConferenceDialog from "./form-confirm-conference-dialog.component";
import FormConfirmDialog from "./form-confirm-dialog.component";
import FormConfirmWarningDialog from "./form-confirm-warning-dialog.component";
import {
  conferenceBookingUsers,
  getUsersIfEmpty,
  handleToBookingServiceDto,
  transformBookingUsers,
  weekdayInput
} from "./functions/form-confirm.functions";
import { useRemoteInactivateBookings } from "../../hooks/Remote/Booking/useRemoteInactivateBookings";
import { bookingStrategyMap } from "./BookingConfirmStrategy/bookingStrategies";
import { teamVisitorGroupPlaceStrategy } from "./BookingConfirmStrategy/teamVisitorGroupPlaceStrategy";
import { visitorBookingStrategy } from "./BookingConfirmStrategy/visitorBookingStrategy";
import { teamWorkplaceUsesVisitorGroupApi } from "./functions/team-visitor-group-booking.functions";
import { checkModeVehicle } from "../../features/Booking-Form/functions/booking-form.functions";
import { FormVehicleConfirm } from "./form-vehicle-confirm-booking.component";
import { FormVehicleTripReasonField } from "./partial/form-vehicle-trip-reason-field.partial";
import { BookableBookingObjectDto } from "../../features/Schedule/typings/resource-calendar.types";
import { AffectedExistingBooking } from "../../features/Booking-Form/typings/cancel-existing-booking.types";
import { FormVisitorConfirm } from "./form-visitor-confirm-booking.component";

/**
 * @description Confirm component in the booking process which displays selected data during the booking process.
 * @version 0.1.2
 */

type Props = {
  row?: BookableBookingObjectDto;
  isResourceBooking?: boolean;
  onResourceBookingClose?: () => void;
  errorToState: (error: string) => void;
};

export const FormConfirm: React.FC<Props> = ({
  row = undefined,
  isResourceBooking = false,
  onResourceBookingClose,
  errorToState
}) => {
  const { t } = useTranslation();
  const { inputs } = useSelector((state: RootState) => state.booking);
  const {
    userInformation: { sub, pricingModels },
    userRoles: { isFleetAdmin, isTenantAdmin },
    settings: { timezone }
  } = useSelector((state: RootState) => state.login);
  const dispatch = useDispatch();

  const [warningOpen, setWarningOpen] = useState(false);
  const [showConfirmAnim, setShowConfirmAnim] = useState(false);
  const [showErrorAnim, setShowErrorAnim] = useState(false);
  const [showCancelExistingConfirm, setShowCancelExistingConfirm] = useState(false);
  const [affectedExistingBookings, setAffectedExistingBookings] = useState<
    AffectedExistingBooking[]
  >([]);
  const cancelExistingRetryRef = useRef<(() => void) | null>(null);
  const [timezoneMobileWorking, setTimezoneMobileWorking] = useState<string>(timezone);

  const [open, setOpen] = useState({ conference: false });
  const [confirmingBooking, setConfirmingBooking] = useState(false);
  const [conferenceBooking, setConferenceBooking] = useState(false);
  const [users, setUsers] = useState<UserOrEmailMapping[]>([]);
  const initialStateInputs = {
    startDate: inputs.bookingFrom,
    endDate: inputs.bookingTo,
    startTime: inputs.bookingStart,
    endTime: inputs.bookingEnd,
    weekdays: {
      Monday: true,
      Tuesday: true,
      Wednesday: true,
      Thursday: true,
      Friday: true,
      Saturday: false,
      Sunday: false
    },
    visitors:
      users.filter(user =>
        inputs.usersBookedFor?.some(userBooking => userBooking.userId === user.userId)
      ) || [],
    colleagues: transformBookingUsers(
      inputs.usersBookedFor as TeamMember[],
      sub,
      inputs,
      timezoneMobileWorking,
      true
    ),
    availability: 0
  };
  const [conferenceInputs, setConferenceInputs] = useState(initialStateInputs);
  const tripReasonRef = useRef("");
  const isVehicleMode = checkModeVehicle(inputs);
  const isVisitorMode = inputs.mode === BookingType.VISITOR;
  const showVisitorFloorPlan = isVisitorMode && visitorRequestsPlaceOrRoom(inputs);
  const initialTripReasonEmpty = (inputs.tripReason ?? "").trim() === "";
  const [tripReasonEmpty, setTripReasonEmpty] = useState(
    isVehicleMode ? initialTripReasonEmpty : false
  );

  const standardBookingInfo = {
    specificDays: inputs.specificDays,
    frequence: inputs.frequence,
    interval: inputs.interval,
    bookingMonthDay: inputs.bookingMonthDay,
    bookingYearDay: inputs.bookingYearDay
  };

  const standardBookingTimes = {
    startDate: inputs.bookingFrom,
    endDate: inputs.bookingTo,
    startTime: inputs.bookingStart,
    endTime: inputs.bookingEnd
  };

  const handleClose = (reason: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick") return;

    setOpen({ conference: false });
  };

  const handlePromise = async (
    promise: Promise<any>,
    onSuccess: () => void,
    onError: (err: any) => void
  ) => {
    return promise
      .then(() => {
        onSuccess();
      })
      .catch(err => {
        onError(err);
      });
  };

  useEffect(() => {
    getUsersIfEmpty(
      users,
      sub,
      dispatch,
      setUsers,
      setConferenceInputs,
      inputs,
      inputs.usersBookedFor
    );
  }, [sub]);

  useEffect(() => {
    if (open.conference)
      fetchLocationAvailability({
        bookingType: "conferencezone",
        endDate: conferenceInputs.endDate as string,
        startDate: conferenceInputs.startDate as string,
        endTime: conferenceInputs.endTime as string,
        startTime: conferenceInputs.startTime as string,
        locationInventoryId: inputs.selectedLocation as number,
        weekdays: conferenceInputs.weekdays,
        users: transformBookingUsers(
          inputs.usersBookedFor as TeamMember[],
          sub,
          inputs,
          timezoneMobileWorking,
          true
        )
      }).then(r => {
        setConferenceInputs(prevState => ({
          ...prevState,
          availability: r.data[0].numberOfBookableObjects
        }));
      });
  }, [
    conferenceInputs.endDate,
    inputs,
    timezoneMobileWorking,
    sub,
    conferenceInputs.endTime,
    conferenceInputs.startDate,
    conferenceInputs.startTime,
    conferenceInputs.weekdays,
    open.conference,
    inputs.selectedLocation,
    inputs.usersBookedFor
  ]);

  const { mutate: inactivateBookings } = useRemoteInactivateBookings();

  const handleInactivateBookings = (inputs: NotSerializedBookingInputs) => {
    if (inputs.deletedRestrictionObjects && inputs.deletedRestrictionObjects.length > 0) {
      inactivateBookings({
        bookings: [
          {
            deleted: false,
            bookingIds: inputs.deletedRestrictionObjects.map((el: any) => el.id),
            bookingType: BookingType.RESTRICTED // type should be checked later
          }
        ]
      });
    }
  };

  const nextStep = useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault();

      const tripReasonValue = ((tripReasonRef.current || inputs.tripReason) ?? "").trim();
      if (checkModeVehicle(inputs) && tripReasonValue === "") {
        return;
      }

      setConfirmingBooking(true);

      const effectiveInputs = checkModeVehicle(inputs)
        ? { ...inputs, tripReason: (tripReasonRef.current || inputs.tripReason) ?? "" }
        : inputs;
      const ctx = {
        inputs: effectiveInputs,
        sub,
        isFleetOrTenantAdmin: isFleetAdmin || isTenantAdmin,
        bookingForUserId: effectiveInputs.usersBookedFor?.[0]?.userId ?? sub,
        timezoneMobileWorking,
        conferenceInputs,
        conferenceBooking,
        standardBookingInfo,
        standardBookingTimes,
        transformBookingUsers,
        handlePromise,
        handleToBookingServiceDto,
        weekdayInput,
        conferenceBookingUsers,
        onSuccess: () => {
          setShowConfirmAnim(true);
          setConfirmingBooking(false);
        },
        onError: (err: any) => {
          setShowErrorAnim(true);
          setConfirmingBooking(false);
          errorToState(err?.response?.data?.message);
        },
        onCancelExistingConfirmationRequired: (
          affected: AffectedExistingBooking[],
          retry: () => void
        ) => {
          setAffectedExistingBookings(affected);
          cancelExistingRetryRef.current = retry;
          setShowCancelExistingConfirm(true);
          setConfirmingBooking(false);
        }
      };

      const defaultType = inputs.bookingType ?? BookingType.WORKPLACE;
      const isActivityBased = inputs.activityBasedBooking ? "ACTIVITY" : "STANDARD";
      const key =
        inputs.bookingType === BookingType.CONFERENCEZONE
          ? `${BookingType.CONFERENCEZONE}_${isActivityBased}`
          : defaultType;

      if (teamWorkplaceUsesVisitorGroupApi(effectiveInputs)) {
        teamVisitorGroupPlaceStrategy(ctx);
      } else if (effectiveInputs.mode === BookingType.VISITOR) {
        // `bookingType` may be WORKPLACE when guests book desks (location APIs); writes still use POST /booking/visitor.
        visitorBookingStrategy(ctx);
      } else {
        bookingStrategyMap[key]?.(ctx);
      }

      // if deletedRestrictionObjects exists
      handleInactivateBookings(inputs);
    },
    [inputs]
  );

  useEffect(() => {
    if (isVehicleMode) {
      setTripReasonEmpty((inputs.tripReason ?? "").trim() === "");
    }
  }, [inputs.tripReason, isVehicleMode]);

  useEffect(() => {
    // This step is skipped when activity based booking is chosen
    if (inputs.activityBasedBooking) {
      nextStep();
    }
  }, [inputs.activityBasedBooking, nextStep]);

  const renderConfirmRightColumn = () => {
    // Vehicle mode
    if (isVehicleMode) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Trip reason first on mobile so it’s visible without scrolling; after vehicle on desktop */}
          <Box sx={{ mt: { md: 1 } }}>
            <FormVehicleTripReasonField
              valueRef={tripReasonRef}
              initialValue={inputs.tripReason ?? ""}
              error={tripReasonEmpty}
              helperText={tripReasonEmpty ? t("_formConfirm_tripReasonRequired") : undefined}
              onValidityChange={setTripReasonEmpty}
            />
          </Box>
          <Box sx={{ mt: { md: 2 } }}>
            <FormVehicleConfirm confirmingBooking={confirmingBooking} bookingInputs={inputs} />
          </Box>
        </Box>
      );
    }

    // Visitor mode
    if (isVisitorMode) {
      const visitorConfirmSpinnerInInfoPanel = !showVisitorFloorPlan;

      return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box data-testid="visitor-confirm-info-container" sx={{ mt: 1 }}>
            <FormVisitorConfirm
              confirmingBooking={visitorConfirmSpinnerInInfoPanel && confirmingBooking}
              bookingInputs={inputs}
            />
          </Box>
          {showVisitorFloorPlan && (
            <Box data-testid="visitor-confirm-floorplan-container" sx={{ mt: 1 }}>
              <FormConfirmFloorPlan confirmingBooking={confirmingBooking} />
            </Box>
          )}
        </Box>
      );
    }

    // Default: floor plan to show place/room (non-vehicle, non-visitor)
    return (
      <FormConfirmFloorPlan
        confirmingBooking={confirmingBooking}
        floorInventoryId={row?.floorInventoryId ?? inputs.floorInventoryId ?? undefined}
      />
    );
  };

  return (
    <>
      <Grid2 container spacing={3} data-testid="form-confirm-parent">
        <Grid2 size={{ md: 4, lg: 3, xs: 12 }}>
          {(inputs.mode === "workplace" || inputs.mode === "team") &&
            !inputs.isMobileWorking &&
            pricingModels.includes(PricingModel.ENTERPRISE) && (
              <Grid2 container justifyContent={"space-between"}>
                <ToggleButton
                  value="auto"
                  selected={conferenceBooking}
                  onClick={() => setOpen({ conference: true })}
                  data-testid="conference-dialog-open-btn"
                >
                  <ConferenceIcon style={{ width: "30px", height: "20px" }} />
                </ToggleButton>
              </Grid2>
            )}

          {/** Card to display a summary and the button to allow booking */}
          <FormConfirmData
            confirmingBooking={confirmingBooking}
            showConfirmAnim={showConfirmAnim}
            nextStep={nextStep}
            onChangeTimezone={newTimezone => setTimezoneMobileWorking(newTimezone)}
            confirmDisabled={isVehicleMode && tripReasonEmpty}
          />
        </Grid2>

        <Grid2 size={{ md: 8, lg: 9, xs: 12 }} data-testid="form-confirm-floorplan-container">
          {inputs.deletedRestrictionObjects && inputs.deletedRestrictionObjects.length > 0 && (
            <TableContainer component={Paper}>
              <Typography>Will be deleted</Typography>
              <Table size="small" aria-label="Bookings that will be deleted">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("Restriction Object")}</TableCell>
                    <TableCell>{t("Zone")}</TableCell>
                    <TableCell>{t("Date")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inputs.deletedRestrictionObjects.map(row => (
                    <TableRow key={row.id}>
                      <TableCell component="th" scope="row">
                        {row.zoneBookingObject ?? t("Restricted by user(s)")}
                      </TableCell>
                      <TableCell>{row.zoneScheduleId}</TableCell>
                      <TableCell>{DateTime.fromISO(row.start).toFormat("dd.MM.yyyy")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {renderConfirmRightColumn()}
        </Grid2>
      </Grid2>

      {/* dialog to confirm conference, deprecated */}
      <FormConfirmConferenceDialog
        setConferenceBooking={setConferenceBooking}
        conferenceBooking={conferenceBooking}
        setConferenceInputs={setConferenceInputs}
        conferenceInputs={conferenceInputs}
        inputs={inputs}
        users={users}
        handles={{
          handleClose: handleClose,
          setOpen: setOpen,
          open: open,
          setWarningOpen: setWarningOpen
        }}
      />

      {/* dialog to inform booking response */}
      <FormConfirmDialog
        showConfirmAnim={showConfirmAnim}
        showErrorAnim={showErrorAnim}
        showCancelExistingConfirm={showCancelExistingConfirm}
        affectedExistingBookings={affectedExistingBookings}
        confirmingBooking={confirmingBooking}
        setOpen={setOpen}
        onSuccessAction={isResourceBooking ? onResourceBookingClose : undefined}
        onCancelExistingProceed={() => {
          setShowCancelExistingConfirm(false);
          setConfirmingBooking(true);
          cancelExistingRetryRef.current?.();
        }}
        onCancelExistingDismiss={() => {
          setShowCancelExistingConfirm(false);
          setAffectedExistingBookings([]);
          cancelExistingRetryRef.current = null;
        }}
      />

      {/* dialog to inform less spots available than selected */}
      <FormConfirmWarningDialog
        warningOpen={warningOpen}
        setWarningOpen={setWarningOpen}
        open={open}
        setOpen={setOpen}
        conferenceBooking={conferenceBooking}
        setConferenceBooking={setConferenceBooking}
      />
    </>
  );
};

export default FormConfirm;
